import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import type { ProjectStats } from '@/lib/reports';
import { formatMinutes } from '@/lib/reports';

ChartJS.register(ArcElement, Tooltip, Legend);

interface ProjectBreakdownProps {
  stats: ProjectStats[];
}

export function ProjectBreakdown({ stats }: ProjectBreakdownProps) {
  const colors = [
    'rgba(59, 130, 246, 0.8)',   // blue
    'rgba(16, 185, 129, 0.8)',   // green
    'rgba(139, 92, 246, 0.8)',   // purple
    'rgba(236, 72, 153, 0.8)',   // pink
    'rgba(251, 191, 36, 0.8)',   // yellow
    'rgba(99, 102, 241, 0.8)',   // indigo
    'rgba(239, 68, 68, 0.8)',    // red
    'rgba(20, 184, 166, 0.8)',   // teal
  ];

  const chartData = {
    labels: stats.map((s) => s.projectName),
    datasets: [
      {
        data: stats.map((s) => s.totalHours),
        backgroundColor: colors.slice(0, stats.length),
        borderColor: '#fff',
        borderWidth: 2,
        hoverOffset: 10,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          font: {
            size: 12,
            family: "'Noto Sans JP', sans-serif",
          },
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: (context: any) => {
            const stat = stats[context.dataIndex];
            return [
              `${stat.projectName}`,
              `${stat.totalHours}時間 (${stat.totalMinutes}分)`,
              `${stat.recordCount}件 (${stat.percentage}%)`,
            ];
          },
        },
      },
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
      <h2 className="text-xl font-bold text-gray-800 mb-6">プロジェクト別稼働時間</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ドーナツグラフ */}
        <div className="h-80">
          {stats.length > 0 ? (
            <Doughnut data={chartData} options={options} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              データがありません
            </div>
          )}
        </div>

        {/* 統計テーブル */}
        <div className="overflow-auto max-h-80">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">プロジェクト</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">時間</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">件数</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">割合</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">報酬</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {stats.map((stat, index) => (
                <tr key={stat.projectId} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: colors[index % colors.length] }}
                      ></div>
                      <div>
                        <div className="font-medium text-gray-900">{stat.projectName}</div>
                        {stat.projectCode && (
                          <div className="text-xs text-gray-500">{stat.projectCode}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold text-gray-900">{stat.totalHours}h</div>
                    <div className="text-xs text-gray-500">{formatMinutes(stat.totalMinutes)}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">{stat.recordCount}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {stat.percentage}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {stat.totalAmount !== undefined && stat.totalAmount > 0 ? (
                      <div className="font-semibold text-green-700">
                        ¥{stat.totalAmount.toLocaleString()}
                      </div>
                    ) : (
                      <div className="text-gray-400 text-xs">-</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
